import React, { useState, useRef, useEffect } from 'react';
import API from './config';

/**
 * Champ « adresse » avec autocomplétion via la Base Adresse Nationale (proxy backend
 * /adresses/recherche). L'utilisateur tape le début d'une adresse, choisit dans la liste,
 * et `onPick` reçoit { adresse, code_postal, ville } pour préremplir les champs voisins.
 *
 * Composant volontairement non couplé au formulaire : il ne gère que le champ adresse et
 * sa liste de suggestions, le parent décide quoi faire des champs CP / ville.
 *
 * Props :
 *  - apiFetch     : wrapper fetch authentifié du parent
 *  - value        : valeur courante du champ adresse
 *  - onChangeText : (texte) => void, frappe libre
 *  - onPick       : (suggestion) => void, sélection d'une adresse de la liste
 *  - inputStyle   : style à appliquer à l'input (réutilise celui du formulaire)
 *  - placeholder  : placeholder de l'input
 */
export default function AdresseAutocomplete({ apiFetch, value, onChangeText, onPick, inputStyle, placeholder }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const wrapRef = useRef(null);

  // Ferme la liste sur clic à l'extérieur du composant.
  useEffect(() => {
    const onDocClick = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const handleChange = (txt) => {
    onChangeText(txt);
    if (timer.current) clearTimeout(timer.current);
    if (!txt || txt.trim().length < 3) { setSuggestions([]); setOpen(false); return; }
    // Debounce : on n'interroge la BAN qu'après une courte pause de frappe.
    timer.current = setTimeout(async () => {
      try {
        const res = await apiFetch(`${API}/adresses/recherche?q=${encodeURIComponent(txt.trim())}`);
        const data = await res.json();
        if (res.ok && Array.isArray(data.suggestions) && data.suggestions.length) {
          setSuggestions(data.suggestions);
          setOpen(true);
        } else {
          setSuggestions([]); setOpen(false);
        }
      } catch {
        // L'autocomplétion est un confort, pas un bloquant : on échoue en silence.
        setSuggestions([]); setOpen(false);
      }
    }, 280);
  };

  const choisir = (sg) => {
    onPick(sg);
    setOpen(false);
    setSuggestions([]);
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <input style={inputStyle} value={value || ''} placeholder={placeholder} autoComplete="off"
        onChange={e => handleChange(e.target.value)}
        onFocus={() => { if (suggestions.length) setOpen(true); }} />
      {open && suggestions.length > 0 && (
        <ul style={{
          position: 'absolute', zIndex: 20, top: '100%', left: 0, right: 0, margin: '2px 0 0',
          padding: 0, listStyle: 'none', background: '#FFF', border: '1px solid #E5E7EB',
          borderRadius: '6px', boxShadow: '0 6px 18px rgba(0,0,0,0.12)', maxHeight: '220px', overflowY: 'auto',
        }}>
          {suggestions.map((sg, i) => (
            // onMouseDown (et non onClick) pour sélectionner avant le blur de l'input.
            <li key={i} onMouseDown={() => choisir(sg)}
              style={{ padding: '8px 12px', fontSize: '13px', cursor: 'pointer', borderBottom: i < suggestions.length - 1 ? '1px solid #F3F4F6' : 'none' }}
              onMouseEnter={e => e.currentTarget.style.background = '#F9FAFB'}
              onMouseLeave={e => e.currentTarget.style.background = '#FFF'}>
              {sg.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
